import { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';
import React, { FC, ReactElement, ReactNode, useMemo } from 'react';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import type { FieldValuesResult } from 'store/fieldValue';
import { CustomizedFieldDefinition, customizedFields } from '../withCustomizedFields';

const CustomizedFieldContext = React.createContext<CustomizedFieldDefinition | undefined>(undefined);

/** The field picked by the `customizedFieldName` prop or, without it, by the URL. */
const WithCustomizedField: FC<{
  children?: ReactNode;
  customizedFieldName?: EditableFieldValuesEntityName;
}> = ({ customizedFieldName, children }) => {
  const fieldNameFromUrl = useAppUrlParams(UrlParamName.CUSTOMIZED_FIELD_NAME);
  const fieldName = customizedFieldName ?? fieldNameFromUrl;
  const customizedField = useMemo(() => customizedFields[fieldName], [fieldName]);

  return (
    <CustomizedFieldContext.Provider value={customizedField}>
      {children}
    </CustomizedFieldContext.Provider>
  );
};

const withCustomizedField =
  (Component: React.ComponentType<any>, customizedFieldName?: EditableFieldValuesEntityName) =>
  ({ ...props }): ReactElement => (
    <WithCustomizedField customizedFieldName={customizedFieldName}>
      <Component {...props} />
    </WithCustomizedField>
  );

function useCustomizedField(): CustomizedFieldDefinition {
  const context = React.useContext(CustomizedFieldContext);
  if (context === undefined) {
    throw new Error('useCustomizedField must be used within a CustomizedFieldContextProvider');
  }
  return context;
}

export { WithCustomizedField, withCustomizedField, useCustomizedField };
